import { Injectable, inject } from "@angular/core";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "../../services/tabs/TabService";
import { ObservabilityService } from "../shared/services/observability.service";

// i18n miss-catcher — a DEV-ONLY tool (bootstrapped from AppComponent behind
// `!environment.production`). It complements the automated page-translation
// pipeline by surfacing the hardcoded English strings that pipeline structurally
// can NOT fix on its own (.ts strings outside its allow-list, dynamic [binding]
// literals, low-confidence guesses, excluded high-fan-out components). Those stay
// plain English even on an `i18n-done` page.
//
// Detection leans on the pseudo locale: every KEYED string is rendered wrapped as
// `[…␣]` (see transloco-pseudo.interceptor.ts), so an un-keyed string is exactly
// one whose rendered text is NOT wrapped. A human clicks the real offenders (only
// a human can tell a hardcoded label from legit dynamic data like a risk name).
//
// Attribution is pure-DOM on purpose: the deployed pseudo build enables script
// optimization (angular.json), which strips Angular's ngDevMode code — so the global
// `ng` debug API (getOwningComponent/getHostElement) is absent and class names are
// minified. Instead we record the chain of custom-element ancestors; the compose
// script (which has the static selector index) resolves that chain to the nearest
// non-primitive feature component. Limitation: a string projected into a child (via
// @Input / ng-content) attributes to whichever component's host encloses it.
//
// Captures are deduped by (nearest component selector + slot + string), persisted to
// localStorage, and exported as JSON for scripts/i18n/compose-feedback-issues.mjs.

// Mirror of the pseudo wrapper (transloco-pseudo.interceptor.ts `pseudoize`): a
// keyed string renders as `[<accented>␣]`. Kept in sync by hand — the marker is
// stable (ADR 010) and importing it would drag the interceptor into this dev-only file.
const PSEUDO_OPEN = "[";
const PSEUDO_CLOSE = "␣]";

// Attribute slots we can see in the rendered DOM (matTooltip is NOT reflected to an
// attribute, so it can't be scanned statically — documented limitation).
const ATTR_SLOTS = ["placeholder", "title", "aria-label", "alt"] as const;
type Slot = "text" | (typeof ATTR_SLOTS)[number];

// Runtime first-party approximation: skip text owned by library components. The
// compose script is the authoritative filter (it drops selectors absent from the
// frontend source index), so this only needs to cut the obvious noise.
const THIRD_PARTY_PREFIXES = ["mat-", "cdk-", "p-", "ng-", "ngx-"];

const STORAGE_KEY = "i18n-miss-catcher:records";
const MARK_ATTR = "data-i18n-mc-candidate"; // set to the slot on outlined elements
const UI_ATTR = "data-i18n-mc-ui"; // our own DOM, excluded from scanning/capture

interface MissRecord {
    text: string;
    slot: Slot;
    selector: string | null; // nearest custom-element ancestor tag (dedup key + display)
    selectorChain: string[]; // custom-element ancestors, nearest-first (compose resolves the real component)
    tag: string;
    role: string | null;
    breadcrumb: string;
    routes: string[];
    count: number;
    firstSeen: string;
    lastSeen: string;
}

@Injectable({ providedIn: "root" })
export class I18nMissCatcherService {
    private records = new Map<string, MissRecord>();
    private active = false;
    private booted = false;
    private rescan = this.debounce(() => this.scanAndOutline(), 200);

    // TabService/Pages are provided in AppModule (the root injector), so a root service
    // can inject them. { optional: true } keeps the tool working (route falls back to the
    // browser location) if those providers ever change — preserving its rip-out-ability.
    private tabService = inject(TabService, { optional: true });
    private pages = inject(Pages, { optional: true });

    // The Sentry boundary lives entirely in ObservabilityService, so this dev-only tool
    // stays free of any @sentry/* import (see submitToSentry / reportI18nMisses).
    private observability = inject(ObservabilityService);

    // Called once from AppComponent in non-prod. Loads persisted records and injects the
    // global outline styles used while capturing. The toggle button + capture panel UI
    // live in NavbarI18nFeedbackComponent, which drives this service. Idempotent.
    public bootstrap(): void {
        if (this.booted) return;
        this.booted = true;
        this.loadRecords();
        this.injectStyles();
    }

    // ── feedback mode (presented + driven by NavbarI18nFeedbackComponent) ─────
    // Whether capture mode is on — bound by the navbar component for its toggle/panel state.
    public get isCapturing(): boolean {
        return this.active;
    }

    // Number of captured strings — shown in the navbar panel.
    public get count(): number {
        return this.records.size;
    }

    public toggle(): void {
        if (this.active) this.disable();
        else this.enable();
    }

    private enable(): void {
        this.active = true;
        document.addEventListener("click", this.onCaptureClick, true);
        document.addEventListener("scroll", this.rescan, true);
        document.addEventListener("keydown", this.onKeydown, true);
        this.mutationObserver.observe(document.body, { childList: true, subtree: true });
        this.scanAndOutline();
    }

    private disable(): void {
        this.active = false;
        document.removeEventListener("click", this.onCaptureClick, true);
        document.removeEventListener("scroll", this.rescan, true);
        document.removeEventListener("keydown", this.onKeydown, true);
        this.mutationObserver.disconnect();
        this.clearOutlines();
    }

    private onKeydown = (e: KeyboardEvent): void => {
        if (e.key === "Escape") this.disable();
    };

    private mutationObserver = new MutationObserver(() => this.rescan());

    // ── scanning / outlining ─────────────────────────────────────────────────
    private scanAndOutline(): void {
        if (!this.active) return;
        this.clearOutlines();
        for (const el of Array.from(document.querySelectorAll<HTMLElement>("body *"))) {
            if (el.closest(`[${UI_ATTR}]`)) continue;
            if (!this.isVisible(el)) continue;
            const slot = this.candidateSlot(el);
            if (slot) el.setAttribute(MARK_ATTR, slot);
        }
    }

    private clearOutlines(): void {
        for (const el of Array.from(document.querySelectorAll(`[${MARK_ATTR}]`))) {
            el.removeAttribute(MARK_ATTR);
        }
    }

    // Returns the slot whose text is an un-keyed candidate, or null. Text nodes win
    // over attributes (they're what a user usually clicks).
    private candidateSlot(el: HTMLElement): Slot | null {
        if (this.isCandidateText(this.directText(el)) && this.isFirstParty(el)) return "text";
        for (const attr of ATTR_SLOTS) {
            const v = el.getAttribute(attr);
            if (v && this.isCandidateText(v) && this.isFirstParty(el)) return attr;
        }
        return null;
    }

    // Concatenated DIRECT text-node children (not descendants) — the text this
    // element itself renders.
    private directText(el: HTMLElement): string {
        let text = "";
        for (const node of Array.from(el.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? "";
        }
        return text.trim();
    }

    private isCandidateText(raw: string): boolean {
        const s = raw.trim();
        if (!s) return false;
        if (this.isPseudoized(s)) return false; // already keyed
        if (!/\p{L}/u.test(s)) return false; // numbers/dates/symbols/whitespace only
        if (s.replace(/[^\p{L}]/gu, "").length <= 2) return false; // 1–2 letters
        return true;
    }

    private isPseudoized(s: string): boolean {
        return s.startsWith(PSEUDO_OPEN) && s.endsWith(PSEUDO_CLOSE);
    }

    private isVisible(el: HTMLElement): boolean {
        if (el.getAttribute("aria-hidden") === "true") return false;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;
        const style = getComputedStyle(el);
        return style.display !== "none" && style.visibility !== "hidden";
    }

    private isFirstParty(el: HTMLElement): boolean {
        return this.ownerChain(el).length > 0;
    }

    // Custom-element ancestors (nearest-first), excluding third-party tags and our own
    // UI. The tags ARE component selectors; compose-feedback-issues.mjs resolves the
    // chain to the nearest one that is a non-primitive feature component.
    private ownerChain(el: HTMLElement): string[] {
        const chain: string[] = [];
        for (let node: HTMLElement | null = el; node; node = node.parentElement) {
            if (node.hasAttribute(UI_ATTR)) return [];
            const tag = node.tagName.toLowerCase();
            if (tag.includes("-") && !THIRD_PARTY_PREFIXES.some((p) => tag.startsWith(p))) {
                chain.push(tag);
            }
        }
        return chain;
    }

    // ── capture ──────────────────────────────────────────────────────────────
    private onCaptureClick = (event: MouseEvent): void => {
        const target = event.target as HTMLElement | null;
        const el = target?.closest<HTMLElement>(`[${MARK_ATTR}]`);
        if (!el || el.closest(`[${UI_ATTR}]`)) return;
        // Swallow the click so the app doesn't navigate/submit while capturing.
        event.preventDefault();
        event.stopPropagation();
        const slot = el.getAttribute(MARK_ATTR) as Slot;
        const text = slot === "text" ? this.directText(el) : (el.getAttribute(slot) ?? "").trim();
        if (text) this.capture(el, slot, text);
    };

    private capture(el: HTMLElement, slot: Slot, text: string): void {
        const selectorChain = this.ownerChain(el);
        const selector = selectorChain[0] ?? null;
        const key = `${selector ?? "?"}|${slot}|${text.replace(/\s+/g, " ")}`;
        const route = this.currentRoute();
        const now = new Date().toISOString();

        const existing = this.records.get(key);
        if (existing) {
            existing.count += 1;
            existing.lastSeen = now;
            if (!existing.routes.includes(route)) existing.routes.push(route);
        } else {
            this.records.set(key, {
                text,
                slot,
                selector,
                selectorChain,
                tag: el.tagName.toLowerCase(),
                role: el.getAttribute("role"),
                breadcrumb: this.breadcrumb(el),
                routes: [route],
                count: 1,
                firstSeen: now,
                lastSeen: now,
            });
        }
        this.saveRecords();
        this.flash(el);
    }

    private breadcrumb(el: HTMLElement): string {
        const parts: string[] = [];
        let node: HTMLElement | null = el;
        for (let i = 0; node && i < 3; i++) {
            const cls = node.classList.length ? `.${node.classList[0]}` : "";
            const id = node.id ? `#${node.id}` : "";
            parts.unshift(`${node.tagName.toLowerCase()}${id}${cls}`);
            node = node.parentElement;
        }
        return parts.join(" > ");
    }

    // The app navigates via internal tabs, so `location` never changes. The URL that
    // identifies the page lives on the active tab; rebuild it exactly like the app's own
    // TabModel.copyUrlToClipboard (`/<lookupKey>[/<id>]`), preferring an explicit tab
    // url/_fullPath (iframe or url-opened pages). Falls back to `location` when there is
    // no active tab (e.g. the login screen).
    private currentRoute(): string {
        const tab = this.tabService?.activeTab;
        if (tab) {
            if (tab.url) return tab.url;
            if (tab._fullPath) return tab._fullPath;
            const key = tab.lookupname ? this.pages?.getReversed(tab.lookupname) : null;
            if (key) {
                const id = tab.id ?? tab.config?.id;
                return `/${key}${id ? `/${id}` : ""}`.toLowerCase();
            }
        }
        return location.pathname + location.search;
    }

    // ── export / persistence ─────────────────────────────────────────────────
    public export(): void {
        const payload = {
            version: 1,
            tool: "i18n-miss-catcher",
            exportedAt: new Date().toISOString(),
            records: Array.from(this.records.values()),
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `i18n-misses-${payload.exportedAt.replace(/[:.]/g, "-")}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }

    // Sends the captured misses to Sentry (one deduped Issue per label). Does NOT clear
    // records, matching export() — the Clear button stays a separate action. Returns the
    // number submitted (0 when Sentry is not enabled). Presented by NavbarI18nFeedbackComponent.
    public submitToSentry(): number {
        const misses = Array.from(this.records.values()).map((r) => ({
            text: r.text,
            slot: r.slot,
            selector: r.selector,
            selectorChain: r.selectorChain,
            routes: r.routes,
            breadcrumb: r.breadcrumb,
            count: r.count,
        }));
        return this.observability.reportI18nMisses(misses);
    }

    public clearAll(): void {
        if (!confirm(`Clear all ${this.records.size} captured strings?`)) return;
        this.records.clear();
        this.saveRecords();
    }

    private loadRecords(): void {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            for (const r of JSON.parse(raw) as MissRecord[]) {
                this.records.set(`${r.selector ?? "?"}|${r.slot}|${r.text.replace(/\s+/g, " ")}`, r);
            }
        } catch {
            /* corrupt storage -> start empty */
        }
    }

    private saveRecords(): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.records.values())));
        } catch {
            /* storage full / private mode -> best effort */
        }
    }

    // ── styles / flash ───────────────────────────────────────────────────────
    private flash(el: HTMLElement): void {
        el.setAttribute("data-i18n-mc-flash", "");
        setTimeout(() => el.removeAttribute("data-i18n-mc-flash"), 400);
    }

    private injectStyles(): void {
        const style = document.createElement("style");
        style.setAttribute(UI_ATTR, "");
        style.textContent = `
            [${MARK_ATTR}] { outline: 2px dashed #d81b60 !important; outline-offset: 1px; cursor: crosshair !important; }
            [data-i18n-mc-flash] { outline: 3px solid #2e7d32 !important; }
        `;
        document.head.append(style);
    }

    private debounce(fn: () => void, ms: number): () => void {
        let t: ReturnType<typeof setTimeout> | undefined;
        return () => {
            if (t) clearTimeout(t);
            t = setTimeout(fn, ms);
        };
    }
}
